import React, { useState, useRef } from 'react';
import { Modal, Button, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useUploadGameCSVMutation } from '../../slices/gameDataApiSlice';
import { validateCsvBasic } from '../../utils/validateCSV.js';

const GameCSVModal = ({
  show,
  onHide,
  gameId,
  homeTeam = '',
  awayTeam = '',
  gameType = 'Mens',
  pendingFiles = [],
  setPendingFiles = () => {},
  mode = 'edit',
}) => {
  const fileInputRef = useRef(null);
  const [uploadGameCSV] = useUploadGameCSVMutation();
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, errors: [] });

  const onFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result;
        const { valid, errors } = validateCsvBasic(text, homeTeam, awayTeam, gameType);
        if (!valid) {
          setErrorModal({ show: true, errors: errors.map((err) => `${file.name}: ${err}`) });
          return;
        }
        setPendingFiles((p) => [...p, file]);
      };
      reader.onerror = () => toast.error(`${file.name}: failed to read file.`);
      reader.readAsText(file);
    });
    e.target.value = '';
  };

  const removeFile = (idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx));

  const uploadAll = async () => {
    let lastResult = null;
    for (let i = 0; i < pendingFiles.length; i++) {
      const formData = new FormData();
      formData.append('file', pendingFiles[i]);
      formData.append('gameId', gameId);
      formData.append('finalize', i === pendingFiles.length - 1);
      lastResult = await uploadGameCSV(formData).unwrap();
    }
    return lastResult;
  };

  const onSave = async () => {
    if (!pendingFiles.length) {
      toast.info('No new files to upload.');
      onHide();
      return;
    }

    setProcessing(true);
    try {
      const uploadResult = await uploadAll();
      setResult(uploadResult);
      setPendingFiles([]);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update CSV.', { position: 'top-right' });
    } finally {
      setProcessing(false);
    }
  };

  const handleResultClose = () => {
    setResult(null);
    onHide();
  };

  const isAddMode = mode === 'add';

  return (
    <>
      <Modal show={show && !result} onHide={onHide} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>{isAddMode ? 'Upload Shot CSV (Optional)' : 'Edit Shot CSV'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-white">
          {processing ? (
            <div className="text-center">
              <Spinner animation="border" variant="warning" />
            </div>
          ) : (
            <>
              {isAddMode ? (
                <Alert className="app-empty-state mb-3">
                  <strong>Instructions:</strong> Select your CSV file, then click "OK" to close this window. After that, click "Add Game" to create the game.
                </Alert>
              ) : (
                <Alert className="app-empty-state mb-3">
                  <strong>Upload CSV:</strong> Select your CSV file and click "Save CSV" to upload them.
                </Alert>
              )}

              <div className="mb-3">
                <Button variant="outline-warning" className="fw-semibold" onClick={() => fileInputRef.current.click()}>
                  Add CSV File
                </Button>
              </div>

              {pendingFiles.length > 0 && (
                <ListGroup className="mb-3">
                  {pendingFiles.map((f, i) => (
                    <ListGroup.Item key={i} className="d-flex justify-content-between bg-dark text-white border-secondary">
                      {f.name}
                      <Button size="sm" variant="outline-warning" className="fw-semibold" onClick={() => removeFile(i)}>
                        Remove
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              <input
                type="file"
                accept=".csv"
                multiple
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={onFileSelect}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          {isAddMode ? (
            <Button variant="warning" className="text-dark fw-semibold" onClick={onHide}>
              OK
            </Button>
          ) : (
            <>
              <Button variant="outline-light" onClick={onHide} disabled={processing}>
                Cancel
              </Button>
              <Button variant="warning" className="text-dark fw-semibold" onClick={onSave} disabled={processing || !pendingFiles.length}>
                {processing ? 'Saving...' : 'Save CSV'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {!isAddMode && (
        <Modal show={!!result} onHide={handleResultClose} centered contentClassName="bg-dark text-white">
          <Modal.Header closeButton closeVariant="white" className="border-secondary">
            <Modal.Title>CSV Upload Result</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-white">
            {result && (
              <div className="csv-result-shell">
                <Alert className="app-empty-state mb-3">
                  {result.message || 'Upload successful!'}
                </Alert>

                <div className="csv-result-stats mb-3">
                  <div className="csv-result-stat">
                    <div className="csv-result-stat-label">Shots Added</div>
                    <div className="csv-result-stat-value">
                      {result.shotsAdded ?? result.insertedCount ?? 0}
                    </div>
                  </div>
                  <div className="csv-result-stat">
                    <div className="csv-result-stat-label">New Players</div>
                    <div className="csv-result-stat-value">
                      {result.newPlayers?.length || 0}
                    </div>
                  </div>
                </div>

                <div className="csv-result-section">
                  <div className="csv-result-section-title">Players Created</div>
                  {result.newPlayers && result.newPlayers.length ? (
                    <ul className="csv-result-player-list mb-0">
                      {result.newPlayers.map((p, i) => (
                        <li key={i} className="csv-result-player-item">
                          <span className="csv-result-player-name">{p.playerName}</span>
                          <span className="csv-result-player-team">{p.team}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-secondary">No new players were created.</div>
                  )}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-secondary">
            <Button variant="warning" className="text-dark fw-semibold" onClick={handleResultClose}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Modal show={errorModal.show} onHide={() => setErrorModal({ show: false, errors: [] })} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>CSV Validation Error</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-white">
          <Alert className="app-empty-state mb-0">
            <strong>The following errors were found:</strong>
            <ul>
              {errorModal.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button variant="warning" className="text-dark fw-semibold" onClick={() => setErrorModal({ show: false, errors: [] })}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GameCSVModal;
